import { useEffect, useRef, useState } from "react";
import { MapPin, Loader2 } from "lucide-react";
import { api } from "../lib/api";

/**
 * REAPI-powered address input with typeahead suggestions.
 *
 * Fires POST /properties/autocomplete on every keystroke (300ms debounce,
 * min 3 chars) so reps can't fat-finger a lookup and burn a REAPI credit
 * on a misspelled address. Selecting a suggestion writes the *full*
 * address string (including city/state/ZIP) back through `onChange`,
 * which is what PropertyDetail expects for a clean single-credit hit.
 *
 * Drop this in anywhere you previously had a plain <Input> address field:
 *   <AddressAutocomplete value={addr} onChange={setAddr} onPick={obj => …}/>
 *
 * Callback `onPick(obj)` (optional) fires with the picked REAPI row so
 * caller can also grab lat/lng/county/zip without a second network hop.
 */
export default function AddressAutocomplete({
  value,
  onChange,
  onPick,
  placeholder = "123 Main St, Phoenix, AZ 85001",
  disabled = false,
  className = "",
  autoFocus = false,
  testid = "address-autocomplete",
  onEnter,      // Called when user presses Enter — usually to trigger Analyze
}) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const debounce = useRef(null);
  const boxRef = useRef(null);

  // Debounced fetch — cancel prior timer on every keystroke so we only
  // hit REAPI when the user pauses typing. 300ms strikes the right
  // balance between "feels instant" and "not spamming credits".
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (!value || value.trim().length < 3) {
      setItems([]);
      return;
    }
    debounce.current = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.post("/properties/autocomplete", {
          search: value,
        });
        setItems(data?.results || []);
        setOpen(true);
        setHighlight(0);
      } catch (_) {
        setItems([]);
      } finally {
        setBusy(false);
      }
    }, 300);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [value]);

  // Click-outside closes the dropdown so a stale list doesn't shadow
  // other UI when the user tabs away.
  useEffect(() => {
    const off = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("mousedown", off);
    return () => window.removeEventListener("mousedown", off);
  }, []);

  const pick = (item) => {
    onChange?.(item.address || item.title || "");
    onPick?.(item);
    setItems([]);
    setOpen(false);
  };

  const onKey = (e) => {
    if (!open || items.length === 0) {
      if (e.key === "Enter") onEnter?.();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pick(items[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className={`relative w-full ${className}`} ref={boxRef}>
      <div className="relative">
        <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          onKeyDown={onKey}
          onFocus={() => value && value.length >= 3 && items.length > 0 && setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          autoFocus={autoFocus}
          data-testid={testid}
          className="w-full h-10 pl-9 pr-9 rounded-full border border-[var(--app-line)] bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-900 disabled:opacity-50"
          autoComplete="off"
        />
        {busy && (
          <Loader2 size={14} className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-slate-400" />
        )}
      </div>
      {open && items.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full rounded-xl border border-[var(--app-line)] bg-white shadow-xl overflow-hidden"
          data-testid={`${testid}-dropdown`}
        >
          {items.map((item, i) => (
            <button
              type="button"
              key={item.id || i}
              onMouseEnter={() => setHighlight(i)}
              onClick={() => pick(item)}
              data-testid={`${testid}-item-${i}`}
              className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 border-b border-slate-100 last:border-0 ${
                i === highlight ? "bg-slate-100" : "hover:bg-slate-50"
              }`}
            >
              <MapPin size={12} className="text-slate-400 shrink-0" />
              <span className="truncate">{item.title || item.address}</span>
              {item.county && (
                <span className="ml-auto text-[10px] text-slate-400 shrink-0">{item.county}</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
